import { useCallback } from 'react';
import { useNavigate } from 'react-router';

import type { ListingApplicationPayload } from 'api/ats';
import { apiCreateListingApplication } from 'api/listingApplication/apiCreateListingApplication';
import type { ApiListingApplicationListingType } from 'api/types/ApiListingApplicationListingType';
import type {
  ApiEventId,
  ApiInternshipId,
  ApiJobId,
  ApiListingApplicationId,
  ApiOrgId,
  ApiVolopId,
} from 'api/types/ApiTypedId';
import type {
  ApiEnvironment,
  ApiUser,
} from 'api/userEnvironment/types/ApiUserEnvironment';
import type { FormSubmitFunctionData } from 'components/Form/FormContext';
import type { I18nLocale } from 'i18n/types/I18nLocale';
import { trackAppliedToListing } from 'modules/listing/analytics/trackAppliedToListing';
import { useJobAppliedConfetti } from 'modules/listing/jobs/components/JobAppliedConfetti/useJobAppliedConfetti';
import { ApiOrgType } from 'modules/listing/orgs/api/types/ApiOrgType';
import { isDefined } from 'utils/functional/type';
import { hasResumeDb } from 'utils/resumeDatabase';

type Args = {
  listingId: ApiJobId | ApiInternshipId | ApiVolopId | ApiEventId;
  listingType: ApiListingApplicationListingType;
  orgId: ApiOrgId | null;
  orgType: ApiOrgType | null;
  locale: I18nLocale;
  onApplied: ((listingApplicationId: ApiListingApplicationId) => void) | null;
};

export function useListingApplicationSubmissionAsUser({
  listingId,
  listingType,
  orgId,
  orgType,
  locale,
  onApplied,
}: Args) {
  const navigate = useNavigate();
  const Confetti = useJobAppliedConfetti();

  const getInitialUserValues = useCallback(
    ({
      user,
      environment,
    }: {
      user: ApiUser;
      environment: ApiEnvironment;
    }) => ({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      resumeS3FileId:
        listingType !== 'VOLOP' ? (user.resumeId ?? '') : undefined,
      consentAts: environment.isEU ? false : undefined,
    }),
    [listingType],
  );

  const submitListingApplicationAsUser = useCallback(
    async ({
      user,
      environment,
      formSubmitData: { values },
    }: {
      user: ApiUser;
      environment: ApiEnvironment;
      formSubmitData: FormSubmitFunctionData<ListingApplicationPayload>;
    }) => {
      const showOnboarding =
        !isDefined(user.isRecruitable) &&
        hasResumeDb(user, environment, locale);

      const newListingApplication = await apiCreateListingApplication(values);
      onApplied?.(newListingApplication.id);

      trackAppliedToListing({
        listingId,
        listingType,
        orgId: orgId ?? null,
        orgType: orgType ?? null,
        isNewResume: user.resumeId !== values.resumeS3FileId,
        hasAts: true,
      });

      const targetPath = showOnboarding
        ? '/dashboard/employment-profile/get-discovered?applied=true'
        : '/dashboard/application-history';

      if (showOnboarding) {
        await navigate(targetPath);
        setTimeout(() => Confetti.showSimple(targetPath), 1000);
      } else {
        setTimeout(() => Confetti.showWithRedirect(targetPath), 1000);
      }
    },
    [
      Confetti,
      listingId,
      listingType,
      locale,
      navigate,
      onApplied,
      orgId,
      orgType,
    ],
  );

  return {
    getInitialUserValues,
    submitListingApplicationAsUser,
  };
}
